import _ from 'underscore';

import {StandardDNACodon} from 'biojs/Codons';
import {ORFMatch} from 'biojs/orfsWithStartCodon';
import {Sequence} from 'components/Sequence';
import * as MapsConstants from 'constants/MapsConstants';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import * as context from 'Context';
import {SelectableAttributes} from 'molbio/maps/selectables/Selectables';
import * as sequtil from 'molbio/SeqUtil';
import BaseTranslation from 'molbio/translations/backbone/BaseTranslation';
import {Optional} from 'util/Types';

export interface ORFAttributes extends ORFMatch, SelectableAttributes {
  visible: boolean;
  color: string;
  hover: boolean;
  start: number;
  end: number;
  layerStart: number;
  layerEnd: number;
  endPosition: number | null;
  annularLayer: number;
  [MapsConstants.LINEAR_LAYER_FULL]: number | undefined;
}

interface ORF {
  get<K extends keyof ORFAttributes>(key: K): ORFAttributes[K];
  set(obj: Partial<ORFAttributes>): ORF;
  set<K extends keyof ORFAttributes>(key: K, value: ORFAttributes[K]): ORF;
  // Defined as part of our backbone extensions.
  fastSet(obj: Partial<ORFAttributes>): ORF;
  fastSet<K extends keyof ORFAttributes>(key: K, value: ORFAttributes[K]): ORF;
}

class ORF extends BaseTranslation {
  defaults(): Partial<ORFAttributes> {
    return _(super.defaults()).extend({
      visible: true,
      color: 'green',
      hover: false,
      startPositions: [],
      endPosition: null, // allowed to be an index or null
      startCodon: null,
    });
  }

  initialize(attributes: ORFMatch, options: {sequence: Sequence}): void {
    const seqlength = options.sequence.length();
    const end = this.get('endPosition');
    // @ts-expect-error: unused variable
    const starts = this.get('startPositions');
    if (end != null) {
      let index;
      if (attributes.strand === 1) {
        index = this.relativeStart(end, 1);
        this.fastSet({start: index, end: (end + 3) % seqlength});
      } else {
        index = this.relativeStart(end, -1);
        this.fastSet({end: sequtil.mod(index + 1, seqlength), start: sequtil.mod(end - 2, seqlength)});
      }
    } else if (attributes.strand === 1) {
      this.fastSet({start: _.min(this.get('startPositions')), end: 0});
    } else {
      this.fastSet({start: 0, end: sequtil.mod(_.max(this.get('startPositions')) + 1, seqlength)});
    }

    if (this.strand() === 1) {
      this.fastSet({layerStart: this.start(), layerEnd: sequtil.mod(this.end(), seqlength)});
    } else {
      this.fastSet({layerStart: sequtil.mod(this.start() - 1, seqlength), layerEnd: this.end()});
    }
  }

  type(): string {
    return 'orf';
  }

  start(): number {
    return this.get('start');
  }

  end(): number {
    return this.get('end');
  }

  layerStart(): number {
    return this.get('layerStart');
  }

  layerEnd(): number {
    return this.get('layerEnd');
  }

  drawStart(): number {
    return this.start();
  }

  drawEnd(): number {
    return this.end();
  }

  startPositions(): Array<number> {
    return this.get('startPositions');
  }

  endPosition(): number | null {
    return this.get('endPosition');
  }

  hover(): boolean {
    return this.get('hover');
  }

  color(): string {
    return this.get('color');
  }

  uid(): string {
    return this.cid;
  }

  strand(): StrandOfDoubleStranded {
    return this.get('strand');
  }

  annularLayer(): number {
    return this.get('annularLayer');
  }

  visible(): boolean {
    return this.get('visible');
  }

  startCodon(): StandardDNACodon {
    return this.get('startCodon');
  }

  protected coerceStartCodonToMethionine(sequence: Sequence): boolean {
    // ORFs are defined as beginning with an initiation codon, so the start codon should always be coerced
    // to methionine.
    return true;
  }

  /**
   * Finds the first start for the shortest ORF that contains `index` on the given strand.
   * Handles wrapping on circular sequences.
   * @param  {[type]} index
   * @param  {Number} direction One of 1/-1 referencing the strand
   * @return {Number}           the position of the 5' end of the start codon.
   */
  relativeStart(index: number, direction: StrandOfDoubleStranded): number {
    const starts = this.startPositions();
    if (direction === 1) {
      // For forward strand, leftmost start.
      // eslint-disable-next-line no-restricted-syntax
      index = _.min(_.filter(starts, (p) => p >= index));
      if (index === Infinity) {
        index = _.min(starts);
      }
    } else {
      // For reverse strand, rightmost start.
      // eslint-disable-next-line no-restricted-syntax
      index = _.max(_.filter(starts, (p) => p <= index));
      if (index === -Infinity) {
        index = _.max(starts);
      }
    }
    return index;
  }

  /**
   * Handle a click on the ORF. Side-effects by manipulating sequence selection.
   * @param sequence The sequence on which this ORF lives
   * @param shiftKey Whether the shift key was pressed when the click was performed
   * @param indexOverride Which base pair was clicked on. This function is often, but not always, called
   *                      within a Context.using populating this value. Pass indexOverride when you cannot or
   *                      do not wish to wrap this call in Context.
   */
  click(sequence: Sequence, shiftKey: Optional<boolean>, indexOverride?: number): void {
    // eslint-disable-next-line one-var
    let end, start;
    if (shiftKey == null) {
      shiftKey = false;
    }
    const selection = sequence.get('selection');
    const index = indexOverride ?? context.get('index');
    if (index != null) {
      if (this.strand() === 1) {
        start = this.relativeStart(index, -1);
        end = this.end();
      } else {
        start = this.start();
        end = sequtil.mod(this.relativeStart(index, 1) + 1, sequence.length());
      }
      if (shiftKey) {
        selection.expandRange(start, end);
      } else {
        selection.select(this, start, end);
      }
    } else if (shiftKey) {
      selection.expandRange(this.start(), this.end());
    } else {
      selection.select(this);
    }
  }

  /**
   * Handle a double-click on the ORF. Side-effects by manipulating sequence selection.
   * @param sequence The sequence on which this ORF lives
   * @param shiftKey Whether the shift key was pressed when the double-click was performed
   * @param indexOverride Which base pair was clicked on. This function is often, but not always, called
   *                      within a Context.using populating this value. Pass indexOverride when you cannot or
   *                      do not wish to wrap this call in Context.
   */
  dblClick(sequence: Sequence, shiftKey: Optional<boolean>, indexOverride?: number): void {
    this.click(sequence, shiftKey, indexOverride);
    if (sequence.allowFeatureEdits()) {
      sequence.get('selection').trigger('orf', this);
    }
  }
}

export default ORF;
